import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon, Button } from '@/components';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { alertRulesSelectors } from '@/redux/selectors';
import {
  alertRulesActions,
  complianceAlertLevelsActions,
} from '@/redux/actions';
import { AlertRule, AlertRuleType } from '@/api';

import { BehavioralAlertsRuleTypeTabs } from './components/BehavioralAlertsRuleTypeTabs/BehavioralAlertsRuleTypeTabs';
import { BehavioralAlertsTable } from './components/BehavioralAlertsTable/BehavioralAlertsTable';
import { BehavioralAlertsDeleteRuleModal } from './components/BehavioralAlertsDeleteRuleModal/BehavioralAlertsDeleteRuleModal';
import { TransferCountRuleModal } from './components/TransferCountRuleModal/TransferCountRuleModal';
import { CommonRuleModal } from './components/CommonRuleModal/CommonRuleModal';
import styles from './BehavioralAlerts.module.sass';
import { DeleteRuleValue } from './components/BehavioralAlertsDeleteRuleModal/BehavioralAlertsDeleteRuleModal.constants';

interface BehavioralAlertsProps {
  isShowDemoData?: boolean;
}

export const BehavioralAlerts = ({ isShowDemoData }: BehavioralAlertsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [deleteRule, setDeleteRule] = useState<DeleteRuleValue | null>(null);
  const [editRuleId, setEditRuleId] = useState<AlertRule['id'] | null>(null);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [activeRuleType, setActiveRuleType] = useState<AlertRuleType>(
    AlertRuleType.COMMON,
  );

  const { data: behavioralAlertsData, loading } = useAppSelector(
    alertRulesSelectors.getAlertRulesData,
  );

  const editingRule = useMemo(
    () =>
      editRuleId
        ? behavioralAlertsData?.data?.find(
            (r) => String(r.id) === String(editRuleId),
          )
        : null,
    [editRuleId, behavioralAlertsData?.data],
  );

  const modalRuleType = editingRule?.type ?? activeRuleType;
  const isModalOpen = !!editRuleId || isAddRuleModalOpen;

  const handleOnDeleteRule = useCallback(
    (id: AlertRule['id']) => {
      if (!id) return;
      dispatch(alertRulesActions.deleteAlertRule({ id }));
      setDeleteRule(null);
    },
    [dispatch],
  );

  useEffect(() => {
    if (!isShowDemoData) {
      dispatch(
        alertRulesActions.listAlertRules({
          limit: 9999,
          offset: 0,
          sort: 'createdAt',
          order: 'desc',
          filter: {},
        }),
      );
      dispatch(complianceAlertLevelsActions.listAlertLevels({}));
    }
  }, [isShowDemoData, dispatch]);

  return (
    <>
      {!isShowDemoData && (
        <div className={styles.actions}>
          <Button
            corner="rect"
            type="primary"
            size="s"
            onClick={() => setIsAddRuleModalOpen(true)}
          >
            <SvgIcon name="plus" />
            <span>{t('translation.add_rule')}</span>
          </Button>
        </div>
      )}

      <div className={styles.alerts}>
        <div className={styles.tabsRow}>
          <BehavioralAlertsRuleTypeTabs
            activeTab={activeRuleType}
            onTabChange={setActiveRuleType}
          />
        </div>

        <div className={styles.content}>
          <BehavioralAlertsTable
            isLoading={loading}
            alertRules={behavioralAlertsData}
            activeRuleType={activeRuleType}
            onAddRule={() => setIsAddRuleModalOpen(true)}
            onEditRule={setEditRuleId}
            onDeleteRule={setDeleteRule}
          />
        </div>
      </div>

      <BehavioralAlertsDeleteRuleModal
        isOpen={!!deleteRule?.id}
        isLoading={loading}
        name={deleteRule?.name ?? ''}
        onClose={() => setDeleteRule(null)}
        onDelete={() => handleOnDeleteRule(deleteRule?.id)}
      />

      <TransferCountRuleModal
        isOpen={isModalOpen && modalRuleType === AlertRuleType.TX_COUNT}
        isLoading={loading}
        editRuleId={
          modalRuleType === AlertRuleType.TX_COUNT ? editRuleId : null
        }
        onClose={() => {
          setEditRuleId(null);
          setIsAddRuleModalOpen(false);
        }}
      />
      <CommonRuleModal
        isOpen={isModalOpen && modalRuleType === AlertRuleType.COMMON}
        isLoading={loading}
        editRuleId={modalRuleType === AlertRuleType.COMMON ? editRuleId : null}
        onClose={() => {
          setEditRuleId(null);
          setIsAddRuleModalOpen(false);
        }}
      />
    </>
  );
};
